// biome-ignore lint/style/useFilenamingConvention: TanStack Router dynamic route parameter

import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { ChatMessageListSkeleton } from "@arcadeai/design-system/components/ui/molecules/chat-message-list";
import { ChatTemplate } from "@arcadeai/design-system/components/ui/templates/chat-template";
import { ChatHeaderSkeleton } from "@arcadeai/design-system/components/ui/templates/chat-template-skeletons";
import {
  ErrorTemplate,
  ErrorTemplateActions,
  ErrorTemplateContent,
  ErrorTemplateDescription,
  ErrorTemplateIcon,
  ErrorTemplateTitle,
  ErrorTemplateUrl,
} from "@arcadeai/design-system/components/ui/templates/error-template";
import { loadChatMessagesForRoute } from "@arcadeai/ui-kit/chat/query-options";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Home, MessageSquarePlus, SearchX } from "lucide-react";
import { PlaygroundChatLayout } from "@/app/playground/chat/chat";
import { sessionQueryOptions } from "@/lib/auth-client";
import { BFF_URL } from "@/lib/const";

const PRELOADED_CHAT_REUSE_MS = 10_000;

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/playground/chat/$chatId"
)({
  // IMPORTANT: This must stay blocking (ensureQueryData, not prefetchQuery).
  // TanStack Router patches replaceState — when onChatPersisted updates
  // the URL, the router re-matches and transitions here. Blocking keeps the
  // previous route mounted while data loads, letting useChat restore
  // seamlessly. Non-blocking + Suspense would flash the skeleton.
  // A chat opened from history is preloaded on hover and reused on click, and
  // its history item shows a spinner while it loads.
  beforeLoad: async ({ params, context }) => {
    const session =
      await context.queryClient.ensureQueryData(sessionQueryOptions);
    const viewerId = session?.user?.id ?? "";
    await loadChatMessagesForRoute({
      baseUrl: BFF_URL,
      chatId: params.chatId,
      queryClient: context.queryClient,
      reuseWithinMs: PRELOADED_CHAT_REUSE_MS,
      viewerId,
    });
  },
  component: PlaygroundChatLayout,
  pendingComponent: ChatWithIdSkeleton,
  notFoundComponent: ChatNotFound,
  loader: () => ({
    crumb: "Chat",
  }),
});

function ChatWithIdSkeleton() {
  return (
    <ChatTemplate>
      <ChatHeaderSkeleton />
      <div className="flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto pt-4">
        <ChatMessageListSkeleton />
      </div>
      <footer className="mx-auto w-full max-w-3xl px-4 pt-2 pb-4">
        <div className="rounded-lg border bg-background p-2 shadow-xs">
          <Skeleton className="h-[44px] w-full rounded-md" />
          <div className="flex justify-end px-2 pt-2 pb-2">
            <Skeleton className="h-9 w-9 rounded-md" />
          </div>
        </div>
      </footer>
    </ChatTemplate>
  );
}

function ChatNotFound() {
  const { organizationId, projectId } = Route.useParams();

  const currentPath = `${window.location.pathname}${window.location.search}`;

  return (
    <ErrorTemplate>
      <ErrorTemplateContent>
        <ErrorTemplateIcon>
          <SearchX className="h-10 w-10 text-muted-foreground/60" />
        </ErrorTemplateIcon>
        <ErrorTemplateTitle>Chat Not Found</ErrorTemplateTitle>
        <ErrorTemplateDescription>
          This chat may have been deleted or the link may be incorrect.
        </ErrorTemplateDescription>
        <ErrorTemplateUrl url={currentPath} />
        <ErrorTemplateActions>
          <Button
            render={
              <Link
                params={{ organizationId, projectId }}
                to="/orgs/$organizationId/projects/$projectId/playground/chat"
              />
            }
            size="lg"
          >
            <MessageSquarePlus className="mr-2 h-4 w-4" />
            New Chat
          </Button>
          <Button
            render={
              <Link
                params={{ organizationId, projectId }}
                to="/orgs/$organizationId/projects/$projectId/home"
              />
            }
            size="lg"
            variant="outline"
          >
            <Home className="mr-2 h-4 w-4" />
            Dashboard
          </Button>
        </ErrorTemplateActions>
      </ErrorTemplateContent>
    </ErrorTemplate>
  );
}
